import { VideoSource } from '@cms/ts/types/enums/VideoSource';
import render from '@tk/utilities/tk.render';

export interface ParsedVideo {
    source: VideoSource | null;
    id: string | null;
}

export function parseVideoUrl(url: string): ParsedVideo {
    try {
        const u = new URL(url);
        const host = u.hostname.replace('www.', '').toLowerCase();

        let source: VideoSource | null = null;
        let id: string | null = null;

        // 🟥 YouTube
        if (host.includes('youtube.com') || host.includes('youtu.be')) {
            source = VideoSource.YOUTUBE;
            if (u.searchParams.has('v')) {
                id = u.searchParams.get('v');
            } else if (u.pathname.startsWith('/embed/')) {
                id = u.pathname.split('/embed/')[1];
            } else if (u.pathname.startsWith('/shorts/')) {
                id = u.pathname.split('/shorts/')[1];
            } else {
                id = u.pathname.slice(1);
            }
        }

        // 🟦 Vimeo
        else if (host.includes('vimeo.com')) {
            source = VideoSource.VIMEO;
            const parts = u.pathname.split('/');
            id = parts.pop() || parts.pop() || null; // handle trailing slash
        }

        // 🟧 Dailymotion
        else if (host.includes('dailymotion.com') || host.includes('dai.ly')) {
            source = VideoSource.DAILYMOTION;
            const match = u.pathname.match(/video\/([^_/?]+)/) || u.pathname.match(/^\/([^_/?]+)/);
            id = match ? match[1] : null;
        }

        // 🎵 TikTok
        else if (host.includes('tiktok.com')) {
            source = VideoSource.TIKTOK;
            const match = u.pathname.match(/\/video\/(\d+)/) || u.pathname.match(/\/v2\/(\d+)/);
            id = match ? match[1] : null;
        }

        // 🔵 Facebook
        else if (host.includes('facebook.com')) {
            source = VideoSource.FACEBOOK;
            if (u.searchParams.has('v')) {
                id = u.searchParams.get('v');
            } else {
                const match = u.pathname.match(/\/videos\/(\d+)/);
                id = match ? match[1] : null;
            }
        }

        return { source, id };
    } catch {
        return { source: null, id: null };
    }
}

export function getVideoValues(value: string) {
    const videoEmbedElement = render<HTMLElement>(value);
    const url = videoEmbedElement.getAttribute('data-url');
    const parsedVideo = url ? parseVideoUrl(url) : { source: null, id: null };

    return {
        source: parsedVideo.source || VideoSource.YOUTUBE,
        sourceId: parsedVideo.id || '',
        thumbnail: videoEmbedElement.getAttribute('data-thumbnail-url') || '',
        title: videoEmbedElement.getAttribute('data-title') || '',
        description: videoEmbedElement.getAttribute('data-description') || '',
        autoplay: videoEmbedElement.hasAttribute('data-autoplay'),
        loop: videoEmbedElement.hasAttribute('data-loop'),
        muted: videoEmbedElement.hasAttribute('data-muted'),
    };
}
